import React, { useState, useMemo } from 'react';
import { useLocale } from '@platform/core';
import { BudgetSubject, BudgetSection, SubjectType } from '../types/budget.types';

// Currency symbol helper
const getCurrencySymbol = (currency: string | undefined): string => {
  switch (currency) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'ILS':
    default: return '₪';
  }
};

// Format number with max 3 decimal places
const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null) return '0';
  return parseFloat(value.toFixed(3)).toString();
};

// Format amount with commas, no decimals
const formatAmount = (value: number | undefined): string => {
  if (value === undefined || value === null) return '0';
  return Math.round(value).toLocaleString('en-US');
};

export interface SubjectsTreeProps {
  subjects: BudgetSubject[];
  sections: BudgetSection[];
  currency?: string;
  intervalType?: string;
  totalIncome?: number;
  periodsCount?: number;

  // CRUD operations
  onAddSubject: (subject: Omit<BudgetSubject, 'id'>) => void;
  onUpdateSubject: (subjectId: string, updates: Partial<BudgetSubject>) => void;
  onRemoveSubject: (subjectId: string) => void;
  onAddSection: (section: Omit<BudgetSection, 'id'>) => void;
  onUpdateSection: (sectionId: string, updates: Partial<BudgetSection>) => void;
  onRemoveSection: (sectionId: string) => void;
  onReorderSubjects?: (subjects: BudgetSubject[]) => void;

  // Optional: for project mode, income is not editable
  readOnlyIncome?: boolean;
  // Optional: show/hide certain features
  showGoals?: boolean;
}

export const SubjectsTree: React.FC<SubjectsTreeProps> = ({
  subjects,
  sections,
  currency = 'ILS',
  intervalType = 'monthly',
  totalIncome = 0,
  periodsCount = 12,
  onAddSubject,
  onUpdateSubject,
  onRemoveSubject,
  onAddSection,
  onUpdateSection,
  onRemoveSection,
  onReorderSubjects,
  readOnlyIncome = false,
  showGoals = true,
}) => {
  const { t, direction } = useLocale();

  // State for editing subject/section names
  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [editingSubjectName, setEditingSubjectName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionName, setEditingSectionName] = useState('');

  // Local state for editing values (save on blur, not on every keystroke)
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  const getEditingValue = (id: string, currentValue: number | undefined): string => {
    if (id in editingValues) return editingValues[id];
    return currentValue?.toString() || '';
  };

  const setEditingValue = (id: string, value: string) => {
    setEditingValues(prev => ({ ...prev, [id]: value }));
  };

  const clearEditingValue = (id: string) => {
    setEditingValues(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Group sections by subject
  const sectionsBySubject = useMemo(() => {
    const grouped: Record<string, BudgetSection[]> = {};
    sections.forEach(section => {
      if (!grouped[section.subjectId]) {
        grouped[section.subjectId] = [];
      }
      grouped[section.subjectId].push(section);
    });
    return grouped;
  }, [sections]);

  // Calculate total goals for each subject from its sections
  const subjectGoalTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    Object.entries(sectionsBySubject).forEach(([subjectId, secs]) => {
      totals[subjectId] = secs.reduce((sum, s) => {
        const goal = parseFloat(String(s.incomePartGoal)) || 0;
        return sum + goal;
      }, 0);
    });
    return totals;
  }, [sectionsBySubject]);

  // Calculate amount per period from percentage
  const calcAmountPerPeriod = (percent: number): number => {
    if (!totalIncome || !periodsCount || !percent) return 0;
    return (totalIncome * percent / 100) / periodsCount;
  };

  const handleAddSubject = (type: SubjectType) => {
    const name = prompt(t('settings.enterSubjectName'));
    if (!name) return;

    onAddSubject({
      name,
      type,
      order: subjects.length,
    });
  };

  const handleAddSection = (subjectId: string) => {
    const name = prompt(t('settings.enterSectionName') || 'Enter section name:');
    if (!name) return;

    onAddSection({
      name,
      subjectId,
      order: (sectionsBySubject[subjectId]?.length || 0),
    });
  };

  // Start editing a subject name
  const startEditingSubject = (subjectId: string, currentName: string) => {
    setEditingSubjectId(subjectId);
    setEditingSubjectName(currentName);
  };

  // Save subject name
  const saveSubjectName = () => {
    if (editingSubjectId && editingSubjectName.trim()) {
      onUpdateSubject(editingSubjectId, { name: editingSubjectName.trim() });
    }
    setEditingSubjectId(null);
    setEditingSubjectName('');
  };

  // Start editing a section name
  const startEditingSection = (sectionId: string, currentName: string) => {
    setEditingSectionId(sectionId);
    setEditingSectionName(currentName);
  };

  // Save section name
  const saveSectionName = () => {
    if (editingSectionId && editingSectionName.trim()) {
      onUpdateSection(editingSectionId, { name: editingSectionName.trim() });
    }
    setEditingSectionId(null);
    setEditingSectionName('');
  };

  // Move subject up in order
  const moveSubjectUp = (subjectId: string) => {
    const subjectsCopy = [...subjects];
    const index = subjectsCopy.findIndex(s => s.id === subjectId);
    if (index <= 0) return;

    [subjectsCopy[index - 1], subjectsCopy[index]] = [subjectsCopy[index], subjectsCopy[index - 1]];
    subjectsCopy.forEach((s, i) => s.order = i);

    if (onReorderSubjects) {
      onReorderSubjects(subjectsCopy);
    }
  };

  // Move subject down in order
  const moveSubjectDown = (subjectId: string) => {
    const subjectsCopy = [...subjects];
    const index = subjectsCopy.findIndex(s => s.id === subjectId);
    if (index < 0 || index >= subjectsCopy.length - 1) return;

    [subjectsCopy[index], subjectsCopy[index + 1]] = [subjectsCopy[index + 1], subjectsCopy[index]];
    subjectsCopy.forEach((s, i) => s.order = i);

    if (onReorderSubjects) {
      onReorderSubjects(subjectsCopy);
    }
  };

  const getSubjectTypeLabel = (type: SubjectType) => {
    switch (type) {
      case 'credit': return t('settings.credit') || 'זכות';
      case 'debit': return t('settings.debit') || 'חובה';
      case 'calculated': return t('settings.calculated') || 'מחושב';
      default: return type;
    }
  };

  return (
    <div className="subjects-tree-container" dir={direction}>
      {showGoals && (
        <div className="section-header-row">
          <h3>{t('settings.subjects') || 'נושאים'}</h3>
          <h3 className="goals-header">{t('settings.goals') || 'יעדים'}</h3>
        </div>
      )}
      <div className="subjects-tree">
        {subjects.map((subject) => {
          const totalDebitGoals = subjects
            .filter(s => s.type === 'debit')
            .reduce((sum, s) => sum + (subjectGoalTotals[s.id] || 0), 0);
          const calculatedGoal = subject.type === 'calculated' ? 100 - totalDebitGoals : null;

          return (
            <div key={subject.id} className={`subject-node type-${subject.type}`}>
              <div className="subject-row">
                <div className="subject-header">
                  {editingSubjectId === subject.id ? (
                    <input
                      type="text"
                      className="inline-edit-input"
                      value={editingSubjectName}
                      onChange={(e) => setEditingSubjectName(e.target.value)}
                      onBlur={saveSubjectName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveSubjectName();
                        if (e.key === 'Escape') { setEditingSubjectId(null); setEditingSubjectName(''); }
                      }}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="subject-name editable-name"
                      onClick={() => startEditingSubject(subject.id, subject.name)}
                      title={t('actions.edit') || 'לחץ לעריכה'}
                    >
                      {subject.name} ({getSubjectTypeLabel(subject.type)})
                    </span>
                  )}
                  {onReorderSubjects && (
                    <div className="reorder-buttons">
                      <button
                        className="btn-icon btn-reorder"
                        onClick={() => moveSubjectUp(subject.id)}
                        disabled={subjects.indexOf(subject) === 0}
                        title={t('actions.moveUp') || 'הזז למעלה'}
                      >
                        ▲
                      </button>
                      <button
                        className="btn-icon btn-reorder"
                        onClick={() => moveSubjectDown(subject.id)}
                        disabled={subjects.indexOf(subject) === subjects.length - 1}
                        title={t('actions.moveDown') || 'הזז למטה'}
                      >
                        ▼
                      </button>
                    </div>
                  )}
                  <button
                    className="btn-icon"
                    onClick={() => handleAddSection(subject.id)}
                    title={t('settings.addSection') || 'הוסף סעיף'}
                  >
                    +
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => {
                      if (confirm(t('actions.confirmDelete'))) {
                        onRemoveSubject(subject.id);
                      }
                    }}
                    title={t('actions.delete')}
                  >
                    ×
                  </button>
                </div>
                {showGoals && (
                  <div className={`goal-column ${subject.type === 'credit' ? 'income-column' : ''}`}>
                    {subject.type === 'credit' && (
                      <div className="income-input-wrapper">
                        <span className="currency-symbol">{getCurrencySymbol(currency)}</span>
                        {readOnlyIncome ? (
                          <span className="income-value">{formatAmount(subject.goalPercentage || totalIncome)}</span>
                        ) : (
                          <input
                            type="number"
                            className="income-input"
                            step="1"
                            value={getEditingValue(`income-${subject.id}`, subject.goalPercentage || totalIncome)}
                            onChange={(e) => setEditingValue(`income-${subject.id}`, e.target.value)}
                            onBlur={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              if (value !== subject.goalPercentage) {
                                onUpdateSubject(subject.id, { goalPercentage: value });
                              }
                              clearEditingValue(`income-${subject.id}`);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                            placeholder="0"
                          />
                        )}
                      </div>
                    )}
                    {subject.type === 'debit' && (
                      <div className="goal-with-amount">
                        <span className="goal-total">{formatNumber(subjectGoalTotals[subject.id])}%</span>
                        <span className="calculated-amount">
                          {getCurrencySymbol(currency)}{formatAmount(calcAmountPerPeriod(subjectGoalTotals[subject.id] || 0))}/{intervalType === 'weekly' ? 'שבוע' : 'חודש'}
                        </span>
                      </div>
                    )}
                    {subject.type === 'calculated' && (
                      <div className="goal-with-amount">
                        <span className="goal-total calculated">{formatNumber(calculatedGoal || 0)}%</span>
                        <span className="calculated-amount">
                          {getCurrencySymbol(currency)}{formatAmount(calcAmountPerPeriod(calculatedGoal || 0))}/{intervalType === 'weekly' ? 'שבוע' : 'חודש'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="sections-list">
                {(sectionsBySubject[subject.id] || []).map((section) => (
                  <div key={section.id} className="section-row">
                    <div className="section-item">
                      {editingSectionId === section.id ? (
                        <input
                          type="text"
                          className="inline-edit-input section-edit"
                          value={editingSectionName}
                          onChange={(e) => setEditingSectionName(e.target.value)}
                          onBlur={saveSectionName}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveSectionName();
                            if (e.key === 'Escape') { setEditingSectionId(null); setEditingSectionName(''); }
                          }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="section-name editable-name"
                          onClick={() => startEditingSection(section.id, section.name)}
                          title={t('actions.edit') || 'לחץ לעריכה'}
                        >
                          {section.name}
                        </span>
                      )}
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => {
                          if (confirm(t('actions.confirmDelete'))) {
                            onRemoveSection(section.id);
                          }
                        }}
                      >
                        ×
                      </button>
                    </div>
                    {showGoals && (
                      <div className="goal-column">
                        <div className="goal-with-amount">
                          <div className="percent-input-wrapper">
                            <input
                              type="number"
                              step="1"
                              value={getEditingValue(`section-${section.id}`, section.incomePartGoal)}
                              onChange={(e) => setEditingValue(`section-${section.id}`, e.target.value)}
                              onBlur={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                if (value !== section.incomePartGoal) {
                                  onUpdateSection(section.id, { incomePartGoal: value });
                                }
                                clearEditingValue(`section-${section.id}`);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              placeholder="0"
                            />
                            <span className="percent-symbol">%</span>
                          </div>
                          <span className="calculated-amount">
                            {getCurrencySymbol(currency)}{formatAmount(calcAmountPerPeriod(parseFloat(getEditingValue(`section-${section.id}`, section.incomePartGoal)) || 0))}/{intervalType === 'weekly' ? 'שבוע' : 'חודש'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {(!sectionsBySubject[subject.id] || sectionsBySubject[subject.id].length === 0) && (
                  <div className="section-row">
                    <div className="no-sections">
                      <button className="btn-link" onClick={() => handleAddSection(subject.id)}>
                        + {t('settings.addFirstSection') || 'הוסף סעיף ראשון'}
                      </button>
                    </div>
                    {showGoals && <div className="goal-column"></div>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <button className="btn btn-add-subject" onClick={() => handleAddSubject('debit')}>
          + {t('settings.addSubject')}
        </button>
      </div>

      <style>{`
        .subjects-tree-container {
          width: 100%;
        }

        .section-header-row {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          border-bottom: 1px solid var(--color-border);
          padding-bottom: 0.5rem;
        }

        .section-header-row h3 {
          margin: 0;
          font-size: 1rem;
        }

        .section-header-row h3:first-child {
          flex: 1;
        }

        .goals-header {
          width: 140px;
          text-align: center;
          background: #f0f9ff;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          font-size: 0.875rem;
        }

        .subjects-tree {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .subject-node {
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          overflow: hidden;
        }

        .subject-node.type-credit {
          border-color: #22c55e;
        }

        .subject-node.type-debit {
          border-color: #ef4444;
        }

        .subject-node.type-calculated {
          border-color: #3b82f6;
        }

        .subject-row, .section-row {
          display: flex;
          align-items: center;
        }

        .subject-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--color-background);
          font-weight: 500;
          flex: 1;
        }

        .subject-name {
          flex: 1;
        }

        .editable-name {
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          transition: background 0.15s;
        }

        .editable-name:hover {
          background: rgba(0, 0, 0, 0.05);
        }

        .inline-edit-input {
          flex: 1;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-primary);
          border-radius: var(--radius);
          font-size: inherit;
          font-weight: inherit;
          outline: none;
        }

        .inline-edit-input.section-edit {
          font-weight: 400;
        }

        .goal-column {
          width: 140px;
          padding: 0.5rem;
          background: #f0f9ff;
          display: flex;
          align-items: center;
          justify-content: center;
          border-right: 1px solid #e0f2fe;
        }

        .goal-column.income-column {
          width: 200px;
          background: #ecfdf5;
        }

        .goal-column input {
          width: 60px;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          text-align: center;
          font-size: 0.875rem;
        }

        .income-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .income-input-wrapper .income-input {
          width: 120px;
          text-align: right;
          font-size: 1rem;
          font-weight: 600;
        }

        .income-value {
          font-weight: 600;
          font-size: 1rem;
        }

        .currency-symbol {
          font-weight: 600;
          color: #166534;
          font-size: 0.875rem;
        }

        .percent-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.125rem;
        }

        .percent-input-wrapper input {
          width: 50px;
        }

        .percent-symbol {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .goal-with-amount {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .calculated-amount {
          font-size: 0.7rem;
          color: #059669;
          font-weight: 500;
          white-space: nowrap;
        }

        .goal-total {
          font-weight: 600;
          color: var(--color-text-secondary);
        }

        .goal-total.calculated {
          color: #3b82f6;
        }

        .sections-list {
          padding: 0;
        }

        .section-row {
          border-top: 1px solid var(--color-border);
        }

        .section-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem 0.5rem 2rem;
          flex: 1;
        }

        .section-name {
          flex: 1;
        }

        .no-sections {
          color: var(--color-text-secondary);
          font-size: 0.875rem;
          padding: 0.5rem 0.75rem 0.5rem 2rem;
          flex: 1;
        }

        .btn-icon {
          width: 24px;
          height: 24px;
          padding: 0;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 1rem;
          line-height: 1;
        }

        .btn-icon:hover {
          background: var(--color-background);
        }

        .btn-icon.btn-danger:hover {
          background: #fee2e2;
          border-color: #ef4444;
          color: #ef4444;
        }

        .reorder-buttons {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .btn-reorder {
          width: 20px;
          height: 16px;
          font-size: 0.625rem;
          color: var(--color-text-secondary);
        }

        .btn-reorder:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .btn-reorder:not(:disabled):hover {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .btn-link {
          background: none;
          border: none;
          color: var(--color-primary);
          cursor: pointer;
          padding: 0;
          font-size: 0.875rem;
        }

        .btn-add-subject {
          margin-top: 0.5rem;
          padding: 0.5rem 1rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-add-subject:hover {
          background: var(--color-background);
        }
      `}</style>
    </div>
  );
};
